import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Popover, Box, Grid } from '@mui/material';
import WSButton from '../Button/WSButton';

type OpenPopoverArgs = {
    anchorEl: HTMLElement | undefined | null;
    Component: React.FC<any>;
    componentProps?: any;
    id?: any;
    showSaveAndNew?: boolean;
    anchorOrigin?: { vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
    transformOrigin?: { vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
};

interface PopoverContextType {
    openPopover: (args: OpenPopoverArgs) => void;
    closePopover: () => void;
}

const PopoverContext = createContext<PopoverContextType | undefined>(undefined);

export const usePopover = () => {
    const context = useContext(PopoverContext);
    if (!context) throw new Error('usePopover must be used within a PopoverProvider');
    return context;
};

export const PopoverProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [Component, setComponent] = useState<React.FC<any> | null>(null);
    const [componentProps, setComponentProps] = useState<any>({});
    const [formId, setFormId] = useState<any>(null);
    const [showSaveAndNew, setShowSaveAndNew] = useState<boolean>(true);
    const [anchorOrigin, setAnchorOrigin] = useState<{ vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' }>({ vertical: 'center', horizontal: 'right' });
    const [transformOrigin, setTransformOrigin] = useState<{ vertical: 'top' | 'center' | 'bottom'; horizontal: 'left' | 'center' | 'right' }>({ vertical: 'center', horizontal: 'left' });
    const openPopover = ({ anchorEl, Component, componentProps, id, showSaveAndNew = true, anchorOrigin, transformOrigin }: OpenPopoverArgs) => {
        setAnchorEl(anchorEl ?? null);
        setComponent(() => Component);
        setComponentProps(componentProps || {});
        setFormId(id || null);
        setShowSaveAndNew(showSaveAndNew);
        if (anchorOrigin) setAnchorOrigin(anchorOrigin);
        if (transformOrigin) setTransformOrigin(transformOrigin);
    };

    const closePopover = () => {
        setAnchorEl(null);
        setComponent(null);
        setFormId(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (componentProps?.onSave) {
            const form = e.target as HTMLFormElement;
            const formData = new FormData(form);
            const data: Record<string, any> = {};

            for (const [key, value] of formData.entries()) {
                data[key] = value;
            }

            componentProps.onSave(data, formId);
        }
    };

    const handleSubmitAndNew = (e: React.FormEvent) => {
        e.preventDefault();
        if (componentProps?.onSaveAndNew) {
            const form = e.target as HTMLFormElement;
            const formData = new FormData(form);
            const data: Record<string, any> = {};

            for (const [key, value] of formData.entries()) {
                data[key] = value;
            }

            componentProps.onSaveAndNew(data, formId);
        }
    };


    return (
        <PopoverContext.Provider value={{ openPopover, closePopover }}>
            {children}

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
            >
                <Box p={2} width="auto">
                    {Component && (
                        <form onSubmit={handleSubmit}>
                            <Component {...componentProps} id={formId} />
                            <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
                                <Grid item>
                                    <WSButton id='btnCancel'
                                        label="Cancel"
                                        type="button"
                                        size='small'
                                        onClick={closePopover}
                                        color='primary'
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 8,
                                            width: "8rem",
                                            border: "1px solid #098963",
                                            color: "#098963",
                                            backgroundColor: "#FFFFFF",
                                        }}
                                    />
                                </Grid>
                                {showSaveAndNew && <Grid item>
                                    <WSButton
                                        id='btnSaveAndNew'
                                        label="Save & New"
                                        type="button"
                                        size='small'
                                        onClick={handleSubmitAndNew}
                                        color='primary'
                                        variant="contained"
                                        sx={{
                                            borderRadius: 8,
                                            width: "8rem",
                                            border: "1px solid #098963",
                                            color: "#FFFFFF",
                                            backgroundColor: "#098963",
                                            "&:hover": {
                                                backgroundColor: "#066e4f",
                                            },
                                        }}
                                    />
                                </Grid>
                                }
                                <Grid item>
                                    <WSButton
                                        id='btnSave'
                                        label="Save"
                                        type="submit"
                                        size='small'
                                        variant="contained"
                                        color='primary'
                                        sx={{
                                            borderRadius: 8,
                                            width: "8rem",
                                            border: "1px solid #098963",
                                            color: "#FFFFFF",
                                            backgroundColor: "#098963",
                                            "&:hover": {
                                                backgroundColor: "#066e4f",
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </form>
                    )}
                </Box>
            </Popover>
        </PopoverContext.Provider>
    );
};
